import type { TFunction } from 'i18next';
import type { ImpactTone } from '~/app/components/dashboard/StatusImpactPill/StatusImpactPill';
import type {
  ServiceQualitySiteStatus,
  ServiceQualityStatsDto,
  SiteSegmentDto,
} from '~/app/services/operator-service-quality/operator-service-quality.types';
import type {
  CustomerRatingCardViewModel,
  LocationRowViewModel,
  SiteSegmentCardViewModel,
} from '~/app/landing/operator/service-quality/ServiceQualityPage.types';

const STATUS_TONE: Record<ServiceQualitySiteStatus, ImpactTone> = {
  critical: 'critical',
  warning: 'warning',
  good: 'good',
};

export function formatSurveyCount(count: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(count);
}

export function formatTrendPercent(delta: number): string {
  const sign = delta > 0 ? '↑' : delta < 0 ? '↓' : '';
  return `${Math.abs(delta)}% ${sign}`.trim();
}

export function mapCustomerRatingCard(
  stats: ServiceQualityStatsDto,
  locale: string,
  t: TFunction<'dashboard'>,
): CustomerRatingCardViewModel {
  const { customerRating } = stats;
  const surveys = formatSurveyCount(customerRating.surveyCount, locale);
  return {
    title: t('serviceQuality.customerRating.title'),
    metric: `${customerRating.rating}/${customerRating.ratingMax}`,
    description: t('serviceQuality.customerRating.description', {
      count: surveys,
    }),
    capacityLeft: t('serviceQuality.customerRating.capacityLeft'),
    capacityRight: t('serviceQuality.customerRating.capacityRight', {
      count: surveys,
    }),
    progressPercent: Math.round(
      (customerRating.rating / customerRating.ratingMax) * 100,
    ),
  };
}

function mapSiteSegment(
  segment: SiteSegmentDto,
  titleKey: string,
  t: TFunction<'dashboard'>,
): SiteSegmentCardViewModel {
  return {
    id: segment.id,
    title: t(titleKey),
    siteCountLabel: t('serviceQuality.segment.siteCount', {
      count: segment.siteCount,
    }),
    sessionSuccessLabel: t('serviceQuality.segment.avgSessionSuccess'),
    sessionSuccessValue: `${segment.avgSessionSuccessPercent}%`,
    sessionSuccessTrend: formatTrendPercent(segment.sessionSuccessDeltaPercent),
    ratingLabel: t('serviceQuality.segment.avgCustomerRating'),
    ratingValue: `${segment.avgCustomerRating}/${segment.ratingMax}`,
    goodStandingLabel: t('serviceQuality.segment.goodStanding'),
    goodStandingValue: t('serviceQuality.segment.goodStandingValue', {
      count: segment.goodStandingSites,
    }),
  };
}

export function mapOwnedSegment(
  stats: ServiceQualityStatsDto,
  t: TFunction<'dashboard'>,
): SiteSegmentCardViewModel {
  return mapSiteSegment(stats.ownedSites, 'serviceQuality.segment.owned', t);
}

export function mapManagedSegment(
  stats: ServiceQualityStatsDto,
  t: TFunction<'dashboard'>,
): SiteSegmentCardViewModel {
  return mapSiteSegment(
    stats.managedSites,
    'serviceQuality.segment.managed',
    t,
  );
}

export function mapLocationRows(
  stats: ServiceQualityStatsDto,
  t: TFunction<'dashboard'>,
): LocationRowViewModel[] {
  return stats.locations.map((row) => ({
    id: row.id,
    location: row.location,
    region: row.region,
    sessionSuccess: `${row.sessionSuccessPercent}%`,
    avgRating: `${row.avgRating} / ${row.ratingMax}`,
    statusTone: STATUS_TONE[row.status],
    statusLabel: t(`serviceQuality.locations.status.${row.status}`),
  }));
}

export function mapEscalationNote(
  stats: ServiceQualityStatsDto,
  t: TFunction<'dashboard'>,
): string {
  const names = stats.escalationNoteSiteIds
    .map(
      (id) => stats.locations.find((location) => location.id === id)?.location,
    )
    .filter((name): name is string => Boolean(name));

  if (names.length === 0) return '';

  return t('serviceQuality.locations.escalationNote', {
    sites: names.join(' and '),
  });
}
